import React, { useState } from 'react';
import { Check, Gift, Mail } from 'lucide-react';
import { useEmailSignup } from '../hooks/useEmailSignup';

// Inline version of the Calum popup offer. Always visible (no cookie gate), and it posts to the
// same waitlist endpoint, so signups get the same welcome email with the kit download.
const KIT = [
  '13-page PDF: the science, 8 daily lifelines and a 7-day plan',
  '5 regulation practices you can use today',
  'Free app access and a nervous-system assessment',
];

export default function StarterKitSignup({ variant = 'section', source = 'Starter Kit Section' }) {
  const [done, setDone] = useState(false);
  const { email, setEmail, loading, error, handleEmailSubmit } = useEmailSignup({
    name: source,
    onSuccess: () => {
      setDone(true);
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', { event_category: 'engagement', method: 'starter_kit_inline' });
      }
    },
  });

  const compact = variant === 'compact';

  return (
    <section
      id={compact ? undefined : 'starter-kit'}
      data-testid={compact ? 'starter-kit-inline' : 'starter-kit-section'}
      className={compact ? 'my-10' : 'py-14 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-slate-50 via-white to-purple-50 scroll-mt-20'}
    >
      <div className={`${compact ? '' : 'max-w-5xl mx-auto'} rounded-3xl bg-gradient-to-br from-slate-900 via-purple-950 to-indigo-950 border border-purple-500/20 shadow-2xl shadow-purple-900/30 overflow-hidden`}>
        <div className={`flex flex-col ${compact ? 'sm:flex-row' : 'md:flex-row'} items-center gap-6 sm:gap-10 p-6 sm:p-10`}>
          <picture className={`shrink-0 ${compact ? 'w-32 sm:w-36' : 'w-40 sm:w-52'}`}>
            <source srcSet="/starter-kit-cover.webp" type="image/webp" />
            <img
              src="/starter-kit-cover.jpg"
              alt="Nervous System Reset starter kit cover"
              width="320"
              height="453"
              loading="lazy"
              className="w-full h-auto rounded-xl shadow-2xl shadow-black/50 ring-1 ring-white/15 -rotate-3"
            />
          </picture>

          <div className="flex-1 w-full text-center sm:text-left">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 text-amber-300 ring-1 ring-amber-300/30 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] mb-3">
              <Gift className="w-3 h-3" /> Free starter kit
            </span>
            <h2 className={`text-white font-extrabold leading-tight mb-4 ${compact ? 'text-2xl' : 'text-2xl sm:text-4xl'}`}>
              Get your free Nervous System Reset kit
            </h2>

            {done ? (
              <p className="text-purple-100/85 text-base leading-relaxed" data-testid="starter-kit-success">
                It&rsquo;s on its way. Check your inbox in the next few minutes.
              </p>
            ) : (
              <>
                <ul className="space-y-2 mb-5 inline-block text-left">
                  {KIT.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm sm:text-[15px] text-purple-100/80 leading-snug">
                      <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-emerald-400/20 ring-1 ring-emerald-300/40 flex items-center justify-center">
                        <Check className="w-3 h-3 text-emerald-300" strokeWidth={3} />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
                <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="starter-kit-form">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    required
                    aria-label="Email address"
                    data-testid="starter-kit-email"
                    className="flex-1 min-w-0 bg-white border border-purple-300 rounded-xl px-4 py-3.5 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-400/30"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="starter-kit-submit"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3.5 rounded-xl font-bold text-sm hover:shadow-lg hover:shadow-purple-600/30 transition-all disabled:opacity-50"
                  >
                    {loading ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Mail className="w-4 h-4" /> Send me my free kit</>}
                  </button>
                </form>
                {error && <p role="alert" className="mt-2 text-sm text-red-200" data-testid="starter-kit-error">{error}</p>}
                <p className="mt-3 text-[11px] text-purple-200/55">Free &middot; No card needed &middot; Lands in your inbox in minutes</p>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
